import { authApi } from '@/features/auth/services/authApi';
import { decodeJwt } from '@/lib/auth/jwt';
import { buildLoginRedirect } from '@/lib/auth/loginRedirect';
import { resolveLogoutDestination } from '@/lib/auth/resolveLogoutDestination';
import { clearAuthStorage, useAuthStore } from '@/stores/authStore';

const REFRESH_LEAD_MS = 90_000;

let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<string> | null = null;

export function cancelProactiveRefresh(): void {
  if (proactiveTimer !== null) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

export function coalescedRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  refreshPromise = authApi
    .refresh(refreshToken)
    .then((res) => {
      const tokens = res.data?.tokens;
      if (!tokens) throw new Error('Malformed refresh response');
      useAuthStore.getState().setTokens({
        accessToken: tokens.accessToken,
        idToken: tokens.idToken,
      });
      // If clearAuth raced the refresh, setTokens above no-op'd via its
      // refreshToken guard. Skip scheduling so we don't strand a zombie
      // timer pointing at a dead session.
      if (useAuthStore.getState().refreshToken) {
        scheduleProactiveRefresh(tokens.accessToken);
      }
      return tokens.accessToken;
    })
    .catch((err: unknown) => {
      // Storage-only wipe + replace(): clearAuth() sync-mutates zustand
      // state, which re-renders the still-mounted protected page with
      // user: null for one frame before unload. See authStore.ts:88-99.
      cancelProactiveRefresh();
      const { user } = useAuthStore.getState();
      const dest = resolveLogoutDestination(user);
      clearAuthStorage();
      window.location.replace(buildLoginRedirect(dest));
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export function scheduleProactiveRefresh(accessToken: string): void {
  cancelProactiveRefresh();
  const payload = decodeJwt(accessToken);
  if (!payload?.exp) return;
  const expMs = payload.exp * 1000;
  const fireIn = expMs - Date.now() - REFRESH_LEAD_MS;
  if (fireIn <= 0) return;
  proactiveTimer = setTimeout(() => {
    void coalescedRefresh().catch(() => undefined);
  }, fireIn);
}
